import { motion } from 'framer-motion';
import { Play, Award, Users, Sparkles } from 'lucide-react';
import Card from '../ui/Card';

export default function SectionAbout({ data }) {
  if (!data) return null;

  return (
    <section id="about" className="section-padding bg-gradient-to-br from-black via-secondary-950 to-secondary-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{data.title}</h2>
        </motion.div>

        {/* Présentation principale */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mb-16"
        >
          <Card className="bg-secondary-900 border-2 border-primary-500/30">
            <div className="grid md:grid-cols-[300px_1fr] gap-8 items-center">
              {/* Photo */}
              <div className="flex justify-center">
                <div className="relative">
                  <img
                    src={data.image}
                    alt="Patrick Sanchez"
                    className="w-64 h-64 object-cover rounded-2xl shadow-2xl border-4 border-primary-500/50"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-primary-500 text-white px-4 py-2 rounded-full font-bold shadow-lg">
                    +15 ans
                  </div>
                </div>
              </div>

              {/* Texte */}
              <div className="space-y-6">
                <p className="text-lg text-secondary-100 leading-relaxed">
                  {data.intro.text}
                </p>

                {/* Baseline */}
                {data.baseline && (
                  <div className="bg-primary-900/30 border-l-4 border-primary-500 p-4 rounded-r-lg">
                    <p className="text-primary-100 font-semibold italic">
                      {data.baseline}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="max-w-6xl mx-auto mb-16"
        >
          <div className="grid md:grid-cols-3 gap-6">
            {data.stats.map((stat, index) => {
              const icons = [Award, Users, Sparkles];
              const Icon = icons[index] || Award;

              return (
                <Card
                  key={index}
                  className="bg-secondary-900 border border-primary-500/20 hover:border-primary-500/50 transition-all text-center"
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 bg-primary-500/20 rounded-full">
                      <Icon size={32} className="text-primary-400" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary-400 mb-2">
                        {stat.value}
                      </div>
                      <div className="text-secondary-200 text-sm leading-relaxed">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </motion.div>

        {/* Showreel */}
        {data.showreel && data.showreel.enabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <Card className="bg-secondary-900 border-2 border-primary-500/30">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Play size={24} className="text-primary-400" />
                  <h3 className="text-2xl font-bold text-white">{data.showreel.title}</h3>
                </div>
                <p className="text-secondary-300 text-sm">
                  Découvrez quelques-unes de mes réalisations
                </p>
              </div>

              <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                <iframe
                  src={data.showreel.embedUrl}
                  title={data.showreel.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </section>
  );
}
